import { useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { BackspaceOutlined } from '@mui/icons-material';
import { StyleObj } from '../../../../@types';

type NumericKeyboardProps = {
  value: string;
  onChange: (value: string) => void;
  onClose?: () => void;
  maxLength?: number;
};

const styles: StyleObj = {
  container: {
    display: 'grid',
    gap: 0.125,
    gridTemplateColumns: 'repeat(3, 1fr)',
    backgroundColor: 'neutral.200',
  },
  button: {
    minHeight: 44,
    fontSize: 28,
    borderRadius: 0,
    fontWeight: 400,
    backgroundColor: 'neutral.100',
    color: 'neutral.600',
    '&:hover': {
      backgroundColor: 'neutral.200',
    },
    '&:active': {
      backgroundColor: 'neutral.300',
    },
  },
};

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

const NumericKeyboard = ({ value, onChange, onClose, maxLength = 10 }: NumericKeyboardProps) => {
  const handleInput = useCallback(
    (key: string) => {
      if (key === '.') {
        if (value.includes('.')) return;

        if (!value) {
          onChange('0.');
          return;
        }
      }

      if (value.length >= maxLength) return;

      if (value.includes('.')) {
        const [, decimals] = value.split('.');
        if (decimals && decimals.length >= 2) return;
      }

      if (value === '0' && key !== '.') {
        onChange(key);
        return;
      }

      onChange(value + key);
    },
    [value, maxLength, onChange]
  );

  const handleDelete = useCallback(() => {
    if (!value) return;
    onChange(value.slice(0, -1));
  }, [value, onChange]);

  const preventFocusLoss = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <Box sx={styles.container}>
      {KEYS.map((key) => (
        <Button key={key} sx={styles.button} onClick={() => handleInput(key)} onMouseDown={preventFocusLoss}>
          {key}
        </Button>
      ))}
      <Button sx={styles.button} onClick={() => handleInput('.')} onMouseDown={preventFocusLoss}>
        .
      </Button>
      <Button sx={styles.button} onClick={() => handleInput('0')} onMouseDown={preventFocusLoss}>
        0
      </Button>
      <IconButton sx={styles.button} onClick={handleDelete} onMouseDown={preventFocusLoss}>
        <BackspaceOutlined />
      </IconButton>
    </Box>
  );
};

export default NumericKeyboard;
